import { Fab, IconButton, Tooltip } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness'
import { useThemeMode } from '../contexts/ThemeModeContext'
import type { ColorMode } from '../contexts/ThemeModeContext'

const MODE_LABELS: Record<ColorMode, string> = {
  auto: 'Auto (system)',
  light: 'Light mode',
  dark: 'Dark mode',
}

function ModeIcon({ colorMode, size }: { colorMode: ColorMode; size?: 'small' }) {
  const props = size ? { fontSize: size as 'small' } : {}
  if (colorMode === 'dark') return <DarkModeIcon {...props} />
  if (colorMode === 'light') return <LightModeIcon {...props} />
  return <SettingsBrightnessIcon {...props} />
}

/** IconButton variant used in AuthLayout (frosted glass in dark mode) */
export function AuthThemeModeButton() {
  const { colorMode, mode, cycleMode } = useThemeMode()
  const theme = useTheme()
  const isDark = mode === 'dark'
  const primaryMain = theme.palette.primary.main

  return (
    <Tooltip title={MODE_LABELS[colorMode]}>
      <IconButton
        onClick={cycleMode}
        aria-label={MODE_LABELS[colorMode]}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          ...(isDark
            ? {
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
              }
            : {
                bgcolor: alpha(primaryMain, 0.08),
                color: 'primary.main',
                border: `1px solid ${alpha(primaryMain, 0.15)}`,
                '&:hover': { bgcolor: alpha(primaryMain, 0.15) },
              }),
        }}
      >
        <ModeIcon colorMode={colorMode} />
      </IconButton>
    </Tooltip>
  )
}

/** Fab variant used in AppLayout */
export function AppThemeModeButton() {
  const { colorMode, mode, cycleMode } = useThemeMode()

  return (
    <Tooltip title={MODE_LABELS[colorMode]}>
      <Fab
        size="small"
        onClick={cycleMode}
        aria-label={MODE_LABELS[colorMode]}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: 'primary.main',
          color: '#fff',
          '&:hover': { bgcolor: 'primary.dark' },
          ...(mode === 'dark' && { border: '1px solid rgba(255,255,255,0.2)' }),
        }}
      >
        <ModeIcon colorMode={colorMode} size="small" />
      </Fab>
    </Tooltip>
  )
}
